import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { PageHero, SectionLabel, EnquiryBanner } from "@/components/site/Sections";
import { Leaf, Recycle, TreePine, Sprout, Check } from "lucide-react";
import { SUSTAINABILITY_POINTS, PAPER_ADVANTAGES } from "@/lib/data";

const ICONS = [Sprout, Recycle, Leaf, TreePine];

const Sustainability = () => (
  <div data-testid="sustainability-page">
    <PageHero
      label="Sustainability"
      title="Paper is the responsible choice."
      subtitle="Renewable, recyclable and biodegradable — we champion paper as a smart, sustainable material for modern business."
    />

    <section className="relative z-10 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="overflow-hidden rounded-sm">
          <img
            src="https://images.unsplash.com/photo-1506452305024-9d3f02d1c9b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxwaW5lJTIwZm9yZXN0JTIwbWlzdHxlbnwwfHx8fDE3ODQwMzk0NjZ8MA&ixlib=rb-4.1.0&q=85"
            alt="Misty pine forest representing renewable, responsibly sourced paper"
            className="w-full h-[320px] lg:h-[500px] object-cover"
          />
        </Reveal>
      </div>
    </section>

    <section className="relative z-10 py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14 max-w-2xl">
          <Reveal><SectionLabel>Why paper matters</SectionLabel></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-forest leading-tight">
              Good for your brand. Good for the planet.
            </h2>
          </Reveal>
        </div>
        <Stagger className="grid gap-px bg-paper-border border border-paper-border rounded-sm overflow-hidden sm:grid-cols-2">
          {SUSTAINABILITY_POINTS.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <StaggerItem key={s.title} className="bg-paper p-9 lg:p-11">
                <Icon className="h-9 w-9 text-forest" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-2xl text-forest">{s.title}</h3>
                <p className="mt-4 text-ink-soft leading-relaxed">{s.body}</p>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>

    <section className="relative z-10 pb-24 lg:pb-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="rounded-sm bg-forest text-paper p-10 lg:p-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionLabel dark>The case for paper</SectionLabel>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
                Why businesses keep choosing paper.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Stagger className="space-y-5">
              {PAPER_ADVANTAGES.map((a) => (
                <StaggerItem key={a} className="flex items-start gap-4 border-b border-paper/15 pb-5">
                  <Check className="h-6 w-6 text-gold shrink-0" />
                  <p className="text-lg text-paper/85">{a}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </div>
    </section>

    <EnquiryBanner />
  </div>
);

export default Sustainability;
